import { FC, useEffect, useState } from 'react';
import { Box, Button, DialogActions, Grid, CircularProgress, ImageList, ImageListItem, ImageListItemBar, IconButton } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { ProductImageUploader } from './ProductImageUploader';
import { useGetAllProductImagesQuery, useDeleteProductImageMutation } from '@services';
import { showToast } from '@store/slices/notification';
import { JimboResponse } from '@models';
import { useAppDispatch } from '@hooks/redux-custom';
import { ConfirmationDialog } from '@components';

type Props = {
  productId: number;
  onClose: () => void;
};

const ProductImages: FC<Props> = ({ productId, onClose }) => {
  const dispatch = useAppDispatch();

  const { data: productImages, isFetching } = useGetAllProductImagesQuery(productId);
  const [deleteProductImage, { isLoading: deleteProductImageIsLoading }] = useDeleteProductImageMutation();

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [deleteImageId, setDeleteImageId] = useState(0);

  useEffect(() => {
    // when delete the last image, auto select the previous one
    if (productImages && selectedImageIndex >= productImages?.length && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  }, [productImages, selectedImageIndex]);

  const selectImage = (index: number) => {
    setSelectedImageIndex(index);
  };

  const handleClose = (isOk: boolean) => {
    setConfirmationDialogOpen(false);

    if (isOk && productId && deleteImageId) {
      deleteProductImage({ productId, imageId: deleteImageId })
        .unwrap()
        .then(() => {
          setDeleteImageId(0);
          dispatch(showToast({ type: 'success', message: 'Successfully deleted the product image!' }));
        })
        .catch((error: { data: JimboResponse }) => {
          setDeleteImageId(0);
          dispatch(showToast({ type: 'error', message: 'Failed to delete product image!' }));
        });
    }
  };

  const handleDeleteProductImage = (imageId: number) => {
    if (productId) {
      setConfirmationDialogOpen(true);
      setDeleteImageId(imageId);
    }
  };

  return (
    <Box>
      {!productImages?.length && isFetching ? (
        <Box sx={{ width: '100%', minWidth: '420px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={productImages?.length ? 7 : 12} sx={{ borderRight: productImages?.length ? '1px solid #eee' : '' }}>
            {productImages?.length ? (
              <Box sx={{ width: '430px', height: '430px' }}>
                <img
                  style={{ border: '1px solid #ccc', width: '100%', objectFit: 'scale-down', maxWidth: '100%', maxHeight: '100%' }}
                  src={`${productImages[selectedImageIndex]?.url_standard}`}
                  alt={productImages[selectedImageIndex]?.image_file || 'Selected product image'}
                  loading="lazy"
                  data-testid="product-images-selected-img"
                />
              </Box>
            ) : (
              <ProductImageUploader productId={productId} displayModel={'large'} />
            )}
          </Grid>
          {!!productImages?.length && (
            <Grid item xs={5} sx={{ minHeight: '420px', width: 400 }}>
              <ImageList sx={{ width: '100%', margin: 0 }} cols={3} rowHeight={97} gap={10}>
                {productImages.map((item, i) => (
                  <ImageListItem
                    key={item.url_thumbnail}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      border: '2px solid #dedede',
                      overflow: 'hidden',
                      borderColor: i === selectedImageIndex ? 'red !important' : '#dedede',
                      height: '97px',
                      width: '97px',
                      transition: 'all 0.3s ease-out',
                      '&:hover': {
                        borderColor: '#b94242',
                        '.image-action-bar': {
                          opacity: 1,
                        },
                      },
                    }}
                    onClick={() => selectImage(i)}
                  >
                    {deleteProductImageIsLoading && deleteImageId === item.id && (
                      <CircularProgress sx={{ position: 'absolute', zIndex: 9 }} />
                    )}

                    <img
                      style={deleteProductImageIsLoading && deleteImageId === item.id ? { opacity: 0.5 } : {}}
                      src={`${item.url_thumbnail}?w=97&h=97&fit=crop&auto=format`}
                      srcSet={`${item.url_thumbnail}?w=97&h=97&fit=crop&auto=format&dpr=2 2x`}
                      alt={item.image_file || `Product image ${i + 1}`}
                      loading="lazy"
                    />
                    <ImageListItemBar
                      className="image-action-bar"
                      sx={{
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                        opacity: 0,
                        transition: 'all 0.3s ease-out',
                      }}
                      position="top"
                      actionIcon={
                        <IconButton
                          sx={{ color: 'white' }}
                          onClick={() => handleDeleteProductImage(item.id)}
                          data-testid="product-images-delete-prod-img-btn"
                        >
                          <CancelIcon />
                        </IconButton>
                      }
                      actionPosition="right"
                    />
                  </ImageListItem>
                ))}
                {productImages?.length < 9 && (
                  <ImageListItem sx={{ height: '97px', width: '97px' }}>
                    <ProductImageUploader productId={productId} displayModel={'small'} />
                  </ImageListItem>
                )}
              </ImageList>
              <ConfirmationDialog
                message={'Are you sure you want to delete this image?'}
                title={'Confirmation'}
                open={confirmationDialogOpen}
                onClose={handleClose}
              />
            </Grid>
          )}
          <DialogActions sx={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button type="button" variant="outlined" data-testid="product-image-modal-cancel" onClick={onClose}>
              Cancel
            </Button>
          </DialogActions>
        </Grid>
      )}
    </Box>
  );
};

export default ProductImages;
